import { apiClient } from './client';
import type {
  UserInfo,
  ProcessDefinition,
  ProcessInstance,
  ProcessListResponse,
  StartProcessRequest,
  Task,
  TaskDetail,
  TaskListResponse,
  CompleteTaskRequest,
  Document,
  AssistantQueryRequest,
  AssistantQueryResponse,
  DocumentCategory,
} from '../types/api.types';
import type { FormDefinition } from '../types/form-schema.types';

// Auth API
export const authApi = {
  getCurrentUser: () => apiClient.get<UserInfo>('/auth/me'),
};

// Process API
export const processApi = {
  getDefinitions: () => apiClient.get<ProcessDefinition[]>('/processes/definitions'),

  getDefinition: (key: string) => apiClient.get<ProcessDefinition>(`/processes/definitions/${key}`),

  startProcess: (key: string, request: StartProcessRequest) =>
    apiClient.post<ProcessInstance>(`/processes/${key}/start`, request),

  getProcesses: (includeEnded = false, authorityId?: string) =>
    apiClient.get<ProcessListResponse>('/processes', {
      params: { includeEnded: String(includeEnded) },
      headers: authorityId ? { 'X-Authority-Id': authorityId } : undefined,
    }),

  getProcess: (id: string) => apiClient.get<ProcessInstance>(`/processes/${id}`),

  getProcessVariables: (id: string) =>
    apiClient.get<Record<string, unknown>>(`/processes/${id}/variables`),

  cancelProcess: (id: string, reason?: string) =>
    apiClient.delete<void>(`/processes/${id}?reason=${encodeURIComponent(reason || '')}`),
};

// Task API
export const taskApi = {
  getMyTasks: (authorityId?: string) => apiClient.get<TaskListResponse>('/tasks', {
    headers: authorityId ? { 'X-Authority-Id': authorityId } : undefined,
  }),

  getTasksByProcess: (processInstanceId: string) =>
    apiClient.get<Task[]>(`/tasks/process/${processInstanceId}`),

  getTask: (id: string) => apiClient.get<Task>(`/tasks/${id}`),

  getTaskDetail: (id: string) => apiClient.get<TaskDetail>(`/tasks/${id}/detail`),

  getTaskForm: (id: string) => apiClient.get<FormDefinition>(`/tasks/${id}/form`),

  claimTask: (id: string) => apiClient.post<void>(`/tasks/${id}/claim`),

  unclaimTask: (id: string) => apiClient.post<void>(`/tasks/${id}/unclaim`),

  completeTask: (id: string, request: CompleteTaskRequest) =>
    apiClient.post<void>(`/tasks/${id}/complete`, request),

  setTaskVariables: (id: string, variables: Record<string, unknown>) =>
    apiClient.put<void>(`/tasks/${id}/variables`, variables),
};

// Document API
export const documentApi = {
  uploadDocument: (
    file: File,
    processInstanceId: string,
    taskId?: string,
    description?: string,
    category?: DocumentCategory
  ) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('processInstanceId', processInstanceId);
    if (taskId) formData.append('taskId', taskId);
    if (description) formData.append('description', description);
    if (category) formData.append('category', category);
    return apiClient.uploadFile<Document>('/documents', formData);
  },

  getDocument: (id: string) => apiClient.get<Document>(`/documents/${id}`),

  downloadDocument: (id: string) => apiClient.downloadFile(`/documents/${id}/download`),

  getDocumentsByProcess: (processInstanceId: string) =>
    apiClient.get<Document[]>(`/documents/process/${processInstanceId}`),

  getDocumentsByTask: (taskId: string) =>
    apiClient.get<Document[]>(`/documents/task/${taskId}`),

  deleteDocument: (id: string) => apiClient.delete<void>(`/documents/${id}`),
};

// Assistant API
export const assistantApi = {
  query: (request: AssistantQueryRequest) =>
    apiClient.post<AssistantQueryResponse>('/assistant/query', request),

  analyzeDocument: (documentId: string, analysisType = 'SUMMARY') =>
    apiClient.post<AssistantQueryResponse>(
      `/assistant/analyze/${documentId}?analysisType=${analysisType}`
    ),
};

// Comment API
export interface Comment {
  id: string;
  processInstanceId: string;
  taskId?: string;
  content: string;
  author: string;
  createdAt: string;
  internal: boolean;
}

export interface CommentListResponse {
  comments: Comment[];
  total: number;
}

export interface CreateCommentRequest {
  processInstanceId: string;
  taskId?: string;
  content: string;
  internal?: boolean;
}

export const commentApi = {
  createComment: (request: CreateCommentRequest) =>
    apiClient.post<Comment>('/comments', request),

  getCommentsByProcess: (processInstanceId: string, includeInternal = false) =>
    apiClient.get<CommentListResponse>(
      `/comments/process/${processInstanceId}?includeInternal=${includeInternal}`
    ),

  getCommentsByTask: (taskId: string) =>
    apiClient.get<CommentListResponse>(`/comments/task/${taskId}`),

  deleteComment: (id: string) => apiClient.delete<void>(`/comments/${id}`),
};
